import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import usePostPages from '../queries/usePostPages';
import updateSearchParams, {
    updateSearchParam,
} from '../../core/util/updateSearchParams';
import useCustomNavigate from '../../core/hooks/router/useCustomNavigate';
import { STORYBLOK_ERROR_MESSAGE } from '../../content/constants';

export interface NewsFilters {
    categories: string[];
}

const parseFilters = (search: string): NewsFilters => {
    const parsedSearch = new URLSearchParams(search);
    return {
        categories: parsedSearch.getAll('categories'),
    };
};

const parseOffset = (search: string, defaultOffset: number): number => {
    const parsedSearch = new URLSearchParams(search);
    const offset = parsedSearch.get('offset');
    return offset ? parseInt(offset, 10) : defaultOffset;
};

const useNewsArchive = (pageSize = 10) => {
    const { search } = useLocation();
    const [offset, setOffset] = useState(() => parseOffset(search, pageSize));
    const navigate = useCustomNavigate();
    const [filters, setFilters] = useState<NewsFilters>(() => {
        return parseFilters(search);
    });

    useEffect(() => {
        setFilters(parseFilters(search));
        setOffset(parseOffset(search, pageSize));
    }, [pageSize, search]);

    const {
        data,
        loading,
        fetchMore: fetchMoreApi,
        error,
    } = usePostPages({
        perPage: offset,
        ...filters,
    });

    if (error) {
        throw new Error(STORYBLOK_ERROR_MESSAGE, { cause: error });
    }

    const fetchMore = async () => {
        await fetchMoreApi({
            page: offset / pageSize + 1,
            perPage: pageSize,
        });
        const updatedParams = updateSearchParam(
            search,
            'offset',
            (offset + pageSize).toString()
        );
        navigate(
            {
                search: updatedParams.toString(),
            },
            { replace: true, preventScrollReset: true }
        );
    };

    const changeFilters = useCallback(
        (values: NewsFilters) => {
            const updatedParams = updateSearchParams(
                search,
                Object.entries(values).map(([key, value]) => ({
                    name: key,
                    value,
                }))
            );
            updatedParams.set('offset', pageSize.toString());
            navigate({
                search: updatedParams.toString(),
            });
        },
        [pageSize, search, navigate]
    );

    return {
        data: data || {
            items: [],
            total: 0,
        },
        loading,
        filters,
        changeFilters,
        fetchMore,
    };
};

export default useNewsArchive;
